import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Card,
  Descriptions,
  Button,
  Space,
  Tag,
  Spin,
  Alert,
  message,
  Row,
  Col,
  Statistic,
  Divider,
} from 'antd';
import {
  EditOutlined,
  DeleteOutlined,
  ArrowLeftOutlined,
  UserOutlined,
  PhoneOutlined,
  MailOutlined,
  IdcardOutlined,
  CarOutlined,
  ClockCircleOutlined,
} from '@ant-design/icons';
import apiService from '../../services/api';
import { formatDate } from '../../utils/helpers';

const DriverDetail = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [driver, setDriver] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState({
    totalJobs: 0,
    completedJobs: 0,
    pendingJobs: 0,
  });

  const fetchDriverDetails = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiService.drivers.getById(id);
      console.log('Full API response:', response);
      console.log('Response data:', response.data);
      console.log('Response data.data:', response.data?.data);

      const driverData = response.data?.data || response.data;
      console.log('Driver data to be set:', driverData);

      if (!driverData) {
        throw new Error('Driver not found');
      }

      setDriver(driverData);
      
      // Get driver's jobs if available
      if (driverData.jobs) {
        const totalJobs = driverData.jobs.length;
        const completedJobs = driverData.jobs.filter(job => job.jobStatus === 'completed').length;
        const pendingJobs = driverData.jobs.filter(job => job.jobStatus === 'pending').length;
        
        setStats({
          totalJobs,
          completedJobs,
          pendingJobs,
        });
      }
    } catch (error) {
      console.error('Error fetching driver details:', error);
      setError(error.response?.data?.message || error.message || 'Failed to load driver details');
      message.error('Failed to load driver details');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchDriverDetails();
  }, [fetchDriverDetails]);

  const handleDelete = async () => {
    try {
      await apiService.drivers.delete(id);
      message.success('Driver deleted successfully');
      navigate('/drivers');
    } catch (error) {
      console.error('Error deleting driver:', error);
      message.error('Failed to delete driver');
    }
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div>
      {error && (
        <Alert
          message="Error"
          description={error}
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
        />
      )}

      <Card
        title={
          <Space>
            <UserOutlined />
            <span>Driver Details</span>
          </Space>
        }
        extra={
          <Space>
            <Button
              type="primary"
              icon={<EditOutlined />}
              onClick={() => navigate(`/drivers/edit/${id}`)}
            >
              Edit
            </Button>
            <Button
              danger
              icon={<DeleteOutlined />}
              onClick={handleDelete}
            >
              Delete
            </Button>
            <Button
              icon={<ArrowLeftOutlined />}
              onClick={() => navigate('/drivers')}
            >
              Back to List
            </Button>
          </Space>
        }
      >
        {driver && (
          <>
            <Row gutter={[16, 16]}>
              <Col span={8}>
                <Card>
                  <Statistic
                    title="Total Jobs"
                    value={stats.totalJobs}
                    prefix={<CarOutlined />}
                  />
                </Card>
              </Col>
              <Col span={8}>
                <Card>
                  <Statistic
                    title="Completed Jobs"
                    value={stats.completedJobs}
                    prefix={<ClockCircleOutlined />}
                    valueStyle={{ color: '#52c41a' }}
                  />
                </Card>
              </Col>
              <Col span={8}>
                <Card>
                  <Statistic
                    title="Pending Jobs"
                    value={stats.pendingJobs}
                    prefix={<CarOutlined />}
                    valueStyle={{ color: '#faad14' }}
                  />
                </Card>
              </Col>
            </Row>

            <Divider orientation="left">Personal Information</Divider>
            
            <Descriptions bordered>
              <Descriptions.Item label="Name" span={3}>
                {driver.name}
              </Descriptions.Item>
              <Descriptions.Item label="Email" span={3}>
                <Space>
                  <MailOutlined />
                  {driver.email}
                </Space>
              </Descriptions.Item>
              <Descriptions.Item label="Phone" span={3}>
                <Space>
                  <PhoneOutlined />
                  {driver.phoneNumber}
                </Space>
              </Descriptions.Item>
              <Descriptions.Item label="License Number" span={3}>
                <Space>
                  <IdcardOutlined />
                  {driver.licenseNumber}
                </Space>
              </Descriptions.Item>
              <Descriptions.Item label="Status" span={3}>
                <Tag color={driver.status === 'available' ? 'success' : driver.status === 'assigned' ? 'processing' : driver.status === 'unavailable' ? 'error' : 'default'}>
                  {driver.status?.toUpperCase() || 'N/A'}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Joined Date" span={3}>
                {formatDate(driver.createdAt)}
              </Descriptions.Item>
            </Descriptions>

            {driver.notes && (
              <>
                <Divider orientation="left">Notes</Divider>
                <Card>
                  <p>{driver.notes}</p>
                </Card>
              </>
            )}
          </>
        )}
      </Card>
    </div>
  );
};

export default DriverDetail; 